import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowLeft, ArrowUpRight, CalendarDays, X, MessageCircle, Tag, Gift, History, ChevronDown } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { RichText } from "@/components/site/RichText";
import { EventCard } from "@/components/site/EventCard";
import { StudentSignup } from "@/components/site/StudentSignup";
import { ChildProgramSignup } from "@/components/site/ChildProgramSignup";
import { PageContentBlocks } from "@/components/site/PageContentBlocks";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { ShareButton } from "@/components/site/ShareButton";

const NEXT_HOLIDAY = { en: "Next holiday", cs: "Nadcházející svátek", he: "החג הקרוב", ru: "Ближайший праздник" };
const NOW_HOLIDAY = { en: "Happening now", cs: "Právě probíhá", he: "חג שמח — עכשיו", ru: "Сейчас праздник" };
const VIEW_HOLIDAY = { en: "View holiday", cs: "Zobrazit svátek", he: "לצפייה בחג", ru: "Смотреть праздник" };

export default function ProgramDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => { if (location.key && location.key !== "default") navigate(-1); else navigate("/"); };
  const { t, tl, lang } = useLang();
  const ref = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });
  const { data: events = [] } = useQuery({
    queryKey: ["events", "upcoming"],
    queryFn: async () => (await api.get("/events?upcoming=true")).data,
  });
  const { data: gallery = [] } = useQuery({
    queryKey: ["gallery"],
    queryFn: async () => (await api.get("/gallery")).data,
  });

  const program = categories.find((c) => c.slug === slug);
  const children = program ? categories.filter((c) => c.parent_id === program.id && !c.hidden) : [];
  const programEvents = program ? events.filter((e) => (e.categories && e.categories.length ? e.categories.includes(program.title) : e.category === program.title)) : [];
  const pastPhotos = program ? gallery.filter((g) => (g.categories && g.categories.length ? g.categories.includes(program.title) : g.category === program.title)) : [];

  const { data: pastEvents = [] } = useQuery({
    queryKey: ["events", "past", program?.title],
    queryFn: async () => (await api.get(`/events?past=true&category=${encodeURIComponent(program.title)}`)).data,
    enabled: !!(showPast && program?.title),
  });

  const localeFor = lang === "he" ? "he-IL" : lang === "ru" ? "ru-RU" : "en-US";
  const fmtShort = (d) => new Date(d + "T00:00:00").toLocaleDateString(localeFor, { month: "short", day: "numeric", year: "numeric" });
  const nextEventFor = (title) =>
    events
      .filter((e) => e.category === title && e.date)
      .sort((a, b) => a.date.localeCompare(b.date))[0] || null;
  const subTarget = (c) => c.link || `/programs/${c.slug}`;

  const holidayDateText = (h) =>
    h?.end_date && h.end_date !== h.date ? `${fmtShort(h.date)} – ${fmtShort(h.end_date)}` : h?.date ? fmtShort(h.date) : "";
  const isHolidayProgram = !!program && program.slug === "holidays-shabbat";
  const { data: upcomingFromApi } = useQuery({
    queryKey: ["holiday", "upcoming"],
    queryFn: async () => (await api.get("/holidays/upcoming")).data,
    enabled: isHolidayProgram,
  });
  const upcomingHoliday = isHolidayProgram && upcomingFromApi && !upcomingFromApi.hidden ? upcomingFromApi : null;

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] text-[#475569]"><EditableText k="eventdetail.loading" as="span" /></div>;
  }
  if (!program) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F9F8F6] gap-4">
        <p className="font-serif text-2xl"><EditableText k="program.notfound" as="span" /></p>
        <Link to="/" className="rounded-full bg-[#0F172A] text-white px-6 py-3 text-sm font-semibold"><EditableText k="program.backhome" as="span" /></Link>
      </div>
    );
  }

  return (
    <div className="relative bg-[#F9F8F6] min-h-screen overflow-x-hidden" data-testid="program-detail-page">
      <div className="grain" />
      <Navbar />

      {/* Hero */}
      <section ref={ref} className="relative pt-24 sm:pt-28">
        <div className="relative h-[52vh] min-h-[380px] overflow-hidden">
          <motion.img
            style={{ y: imgY }}
            src={resolveImg(program.image_url)}
            alt={tl(program, "title")}
            className="absolute inset-0 w-full h-[120%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-[#0F172A]/10" />
          <div className="absolute inset-0 flex items-end">
            <div className="mx-auto max-w-7xl w-full px-5 sm:px-8 pb-10">
              <div className="flex items-center justify-between gap-3 mb-5">
                <button onClick={goBack} data-testid="program-back" className="flex w-fit items-center gap-2 text-sm text-white/80 hover:text-white transition-colors">
                  <ArrowLeft size={16} /> <EditableText k="program.back" as="span" />
                </button>
                <ShareButton path={`programs/${program.slug}`} title={tl(program, "title")} light testid="program-share-btn" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="program.label" as="span" /></span>
              <h1 className="mt-3 font-serif text-4xl sm:text-6xl tracking-tight text-white leading-[0.98] max-w-3xl">{tl(program, "title")}</h1>
            </div>
          </div>
        </div>
      </section>

      {/* About the program */}
      <section className="relative z-10 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <p className="font-serif text-2xl sm:text-3xl leading-snug text-[#0F172A]">{tl(program, "description")}</p>
            {tl(program, "body") && (
              <RichText html={tl(program, "body")} className="mt-6 text-[#475569] text-base sm:text-lg leading-relaxed" />
            )}
            {(program.order_whatsapp || tl(program, "price_note")) && (
              <div data-testid="service-order-cta" className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border border-[#BE923C]/30 bg-[#FBF7EE] p-5 sm:p-6">
                {tl(program, "price_note") && (
                  <div className="flex-1">
                    <span className="inline-flex items-center gap-2 text-[#9E7829] font-semibold">
                      <Tag size={16} /> {tl(program, "price_note")}
                    </span>
                  </div>
                )}
                {program.order_whatsapp && (
                  <a
                    data-testid="service-order-whatsapp"
                    href={`https://wa.me/${((program.whatsapp_number || t("contact.whatsapp")) || "").replace(/\D/g, "")}?text=${encodeURIComponent(tl(program, "order_whatsapp") || program.order_whatsapp)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#a87f31] text-white text-sm font-semibold px-6 py-3.5 transition-colors"
                  >
                    <MessageCircle size={17} /> {tl(program, "price_note") ? t("services.order_whatsapp") : t("services.contact_whatsapp")}
                  </a>
                )}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      <PageContentBlocks page={`program:${slug}`} position="top" />

      {/* Donate a book — library only */}
      {program.slug === "children-s-jewish-books-library" && (
        <section className="relative z-10 pb-4 sm:pb-6" data-testid="library-donate-box">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <Reveal>
              <div className="rounded-3xl border border-[#BE923C]/30 bg-gradient-to-br from-[#FBF7EE] to-[#F5ECD8] p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#BE923C]/15 text-[#9E7829] shrink-0">
                      <Gift size={20} />
                    </span>
                    <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#0F172A]"><EditableText k="library.donate_title" as="span" /></h2>
                  </div>
                  <p className="mt-3 text-[#475569] leading-relaxed"><EditableText k="library.donate_desc" as="span" /></p>
                </div>
                <a
                  data-testid="library-donate-whatsapp"
                  href={`https://wa.me/${((program.book_donate_number || program.whatsapp_number || t("contact.whatsapp")) || "").replace(/\D/g, "")}?text=${encodeURIComponent(t("library.donate_msg"))}`}
                  target="_blank" rel="noopener noreferrer"
                  className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#a87f31] text-white text-sm font-semibold px-6 py-3.5 transition-colors"
                >
                  <MessageCircle size={17} /> <EditableText k="library.donate_cta" as="span" />
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Sub-programs */}
      {children.length > 0 && (
        <section className="relative z-10 pb-16 sm:pb-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-8">{t("program.explore_within")} {tl(program, "title")}</h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {children.map((c, i) => {
                const isActive = c.active !== false;
                const nextEv = nextEventFor(c.title);
                return (
                  <Reveal key={c.id} delay={i * 0.05}>
                    <div
                      data-testid={`subprogram-${c.slug}`}
                      className="group flex flex-col h-full rounded-3xl bg-white border border-[#0F172A]/5 overflow-hidden shadow-[0_8px_30px_rgba(15,23,42,0.06)] hover:shadow-[0_20px_50px_rgba(15,23,42,0.1)] transition-shadow"
                    >
                      <button
                        onClick={() => navigate(subTarget(c))}
                        data-testid={`subprogram-open-${c.slug}`}
                        className="relative h-44 w-full overflow-hidden text-left"
                      >
                        <img src={resolveImg(c.image_url)} alt={tl(c, "title")} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent" />
                        <span
                          data-testid={`subprogram-status-${c.slug}`}
                          className={`absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold shadow-sm ${
                            isActive ? "bg-[#5A8A4A] text-white" : "bg-[#D24B4B] text-white"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-white" : "bg-white/90"}`} />
                          {isActive ? t("program.sub_active") : t("program.sub_inactive")}
                        </span>
                      </button>

                      <div className="flex flex-col flex-1 p-5">
                        <button onClick={() => navigate(subTarget(c))} className="text-left">
                          <h3 className="font-serif text-xl text-[#0F172A] flex items-center gap-1.5 group-hover:text-[#9E7829] transition-colors">
                            {tl(c, "title")}
                            <ArrowUpRight size={17} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                          </h3>
                        </button>
                        <p className="mt-2 text-sm text-[#475569] leading-relaxed line-clamp-3 flex-1">{tl(c, "description")}</p>

                        {nextEv && (
                          <Link
                            to={`/events/${nextEv.slug || nextEv.id}`}
                            data-testid={`subprogram-next-${c.slug}`}
                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FBF7EE] border border-[#BE923C]/25 px-4 py-2 text-sm font-semibold text-[#9E7829] hover:bg-[#f3e9d4] transition-colors"
                          >
                            <CalendarDays size={15} />
                            <span>{t("program.sub_next")}: {fmtShort(nextEv.date)}</span>
                            <ArrowUpRight size={14} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Students Club sign-up (before upcoming events) */}
      {program.slug === "students-club" && <StudentSignup />}
      {program.slug === "students-club" && (
        <section className="relative z-10 py-12" data-testid="student-card-cta">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="rounded-3xl bg-[#0F172A] text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]">{{ en: "Student discount card", cs: "Studentská slevová karta", he: "כרטיס הנחה לסטודנטים", ru: "Студенческая скидочная карта" }[lang] || "Student discount card"}</p>
                <h3 className="mt-2 font-serif text-2xl sm:text-3xl">{{ en: "Save at the Chabad Kosher Shop", cs: "Ušetřete v košer obchodě Chabad", he: "חסכו בחנות הכשרה של חב\"ד", ru: "Экономьте в кошерном магазине Хабад" }[lang] || "Save at the Chabad Kosher Shop"}</h3>
              </div>
              <button onClick={() => navigate("/students/card")} data-testid="student-card-cta-btn" className="shrink-0 rounded-full bg-[#BE923C] hover:bg-[#a87f33] text-white font-semibold px-7 py-3 transition-colors">
                {{ en: "Get your card", cs: "Získat kartu", he: "לקבלת הכרטיס", ru: "Получить карту" }[lang] || "Get your card"} →
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Children's Program registration */}
      {program.slug === "sunday-hebrew-school" && (
        <ChildProgramSignup defaultProgram="sunday" />
      )}

      {/* Flyers */}
      {(program.flyers || []).length > 0 && (
        <section className="relative z-10 py-14 sm:py-16" data-testid="program-flyers">
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <Reveal>
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="program.flyers_eyebrow" as="span" /></span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight mb-8"><EditableText k="program.flyers_title" as="span" /></h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {program.flyers.map((fl, i) => (
                <Reveal key={i} delay={(i % 3) * 0.05}>
                  <button type="button" data-testid={`program-flyer-${i}`} onClick={() => setLightbox(resolveImg(fl))}
                    className="group block w-full overflow-hidden rounded-2xl border border-[#0F172A]/5 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)] hover:shadow-[0_16px_44px_rgba(15,23,42,0.12)] hover:-translate-y-0.5 transition-all">
                    <img src={resolveImg(fl)} alt={`${tl(program, "title")} flyer ${i + 1}`} className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming holiday spotlight — Holidays & Shabbat page */}
      {upcomingHoliday && (
        <section className="relative z-10 pt-4 pb-8 sm:pb-10" data-testid="program-upcoming-holiday">
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <Reveal>
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]">{(upcomingHoliday.date <= new Date().toISOString().slice(0, 10) ? NOW_HOLIDAY : NEXT_HOLIDAY)[lang] || NEXT_HOLIDAY.en}</span>
              <Link
                to={`/holidays/${upcomingHoliday.id}`}
                data-testid="program-upcoming-holiday-link"
                className="group mt-4 block overflow-hidden rounded-3xl border border-[#0F172A]/5 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.08)] hover:shadow-[0_24px_60px_rgba(15,23,42,0.14)] transition-shadow"
              >
                <div className="flex flex-col sm:flex-row">
                  <div className="relative sm:w-2/5 h-56 sm:h-auto sm:min-h-[15rem] overflow-hidden">
                    <img src={resolveImg(upcomingHoliday.image_url)} alt={tl(upcomingHoliday, "name")} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/40 to-transparent sm:bg-gradient-to-r sm:from-[#0F172A]/25" />
                  </div>
                  <div className="flex flex-1 flex-col justify-center p-6 sm:p-9">
                    <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[#BE923C]/25 bg-[#FBF7EE] px-3 py-1.5 text-xs font-semibold text-[#9E7829]">
                      <CalendarDays size={14} /> {holidayDateText(upcomingHoliday)}
                    </span>
                    <h3 className="mt-4 font-serif text-3xl sm:text-4xl leading-[1.05] tracking-tight text-[#0F172A]">{tl(upcomingHoliday, "name")}</h3>
                    {tl(upcomingHoliday, "subtitle") && <p className="mt-2 text-[#475569] leading-relaxed">{tl(upcomingHoliday, "subtitle")}</p>}
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#BE923C] transition-all group-hover:gap-3">
                      {VIEW_HOLIDAY[lang] || VIEW_HOLIDAY.en} <ArrowUpRight size={16} />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* Upcoming events in this program */}
      {!program.hide_events && (
        <section className="relative z-10 py-16 sm:py-20 bg-[#E8E6E1]/50 border-y border-[#0F172A]/5">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="events.eyebrow" as="span" /></span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight mb-8">{t("program.upcoming")} {tl(program, "title")} {t("program.upcoming_suffix")}</h2>
            </Reveal>
            {programEvents.length === 0 ? (
              <p data-testid="program-events-empty" className="text-[#475569]"><EditableText k="program.events_empty" as="span" /></p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {programEvents.map((ev, i) => (
                  <Reveal key={ev.id} delay={i * 0.05}><EventCard event={ev} index={i} /></Reveal>
                ))}
              </div>
            )}

            <div className="mt-10">
              <button
                type="button"
                data-testid="program-past-events-toggle"
                onClick={() => setShowPast((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 bg-white px-6 py-3 text-sm font-semibold text-[#0F172A] transition-colors hover:border-[#BE923C] hover:text-[#BE923C]"
              >
                <History size={16} />
                {showPast ? t("program.past_events_hide") : t("program.past_events")}
                <ChevronDown size={16} className={`transition-transform ${showPast ? "rotate-180" : ""}`} />
              </button>

              {showPast && (
                <div className="mt-8" data-testid="program-past-events">
                  {pastEvents.length === 0 ? (
                    <p data-testid="program-past-events-empty" className="text-[#475569]">{t("program.past_events_empty")}</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {pastEvents.map((ev, i) => (
                        <Reveal key={ev.id} delay={i * 0.04}><EventCard event={ev} index={i} /></Reveal>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Past event photos */}
      {pastPhotos.length > 0 && (
        <section className="relative z-10 py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="program.memories" as="span" /></span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight mb-8"><EditableText k="program.past_photos" as="span" /></h2>
            </Reveal>
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
              {pastPhotos.map((img, i) => (
                <Reveal key={img.id} delay={(i % 4) * 0.05} className="mb-4 break-inside-avoid">
                  <div className="overflow-hidden rounded-2xl group">
                    <img src={resolveImg(img.image_url)} alt={tl(img, "caption")} className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i % 3 === 0 ? "h-72" : "h-56"}`} />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <PageContentBlocks page={`program:${slug}`} position="bottom" />

      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            data-testid="program-flyer-lightbox" onClick={() => setLightbox(null)}>
            <button type="button" data-testid="program-flyer-lightbox-close" onClick={() => setLightbox(null)}
              className="absolute top-4 end-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
              <X size={22} />
            </button>
            <motion.img initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              src={lightbox} alt="" onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl" />
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
